import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Trans, useTranslation } from "react-i18next";
import type { Appointment, Hold, Transaction } from "@passa-a-pagina/shared";
import { useAuth } from "../lib/auth";
import { api, ApiError } from "../lib/api";
import "./dashboard.css";

function DeleteAccountSection() {
  const { t } = useTranslation();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const expectedWord = t("deleteAccount.confirmWord");

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (confirm.trim().toUpperCase() !== expectedWord) {
      setError(t("deleteAccount.errorMismatch"));
      return;
    }
    setSubmitting(true);
    try {
      await api<void>("/api/me/account", { method: "DELETE" });
      // Clear local auth state too
      await logout();
      navigate("/", { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("common.error"));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section aria-labelledby="del-title" className="dashboard__delete">
      <h2 id="del-title">{t("deleteAccount.title")}</h2>
      {!open ? (
        <button type="button" className="btn btn-secondary" onClick={() => setOpen(true)}>
          {t("deleteAccount.title")}
        </button>
      ) : (
        <form onSubmit={onSubmit} className="surface dashboard__delete-form">
          <p role="alert">{t("deleteAccount.warning")}</p>
          <div className="field">
            <label htmlFor="del-confirm">
              {t("deleteAccount.confirmLabel")} (<code>{expectedWord}</code>)
            </label>
            <input
              id="del-confirm"
              type="text"
              autoComplete="off"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              aria-invalid={Boolean(error)}
              aria-describedby={error ? "del-error" : undefined}
              disabled={submitting}
            />
            {error && <p id="del-error" className="field__error" role="alert">{error}</p>}
          </div>
          <div className="dashboard__delete-actions">
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? t("deleteAccount.submitting") : t("deleteAccount.submit")}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => { setOpen(false); setConfirm(""); setError(null); }}
              disabled={submitting}
            >
              {t("common.cancel")}
            </button>
          </div>
        </form>
      )}
    </section>
  );
}

function formatRelative(toIso: string, locale: string): { text: string; soon: boolean } {
  const ms = new Date(toIso).getTime() - Date.now();
  if (ms <= 0) return { text: "0", soon: true };
  const days = Math.floor(ms / 86_400_000);
  const hours = Math.floor((ms % 86_400_000) / 3_600_000);
  const dayLabel = locale.startsWith("en") ? (days === 1 ? "day" : "days") : (days === 1 ? "dia" : "dias");
  if (days >= 1) return { text: `${days} ${dayLabel} ${hours}h`, soon: false };
  const mins = Math.floor((ms % 3_600_000) / 60_000);
  return { text: `${hours}h ${mins}m`, soon: true };
}

function formatDate(iso: string, locale: string): string {
  return new Date(iso).toLocaleDateString(locale === "en" ? "en-GB" : "pt-PT", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

export function Dashboard() {
  const { t, i18n } = useTranslation();
  const { user, loading } = useAuth();

  const txQuery = useQuery({
    queryKey: ["me", "transactions"],
    queryFn: () => api<{ transactions: Transaction[] }>("/api/me/transactions"),
    enabled: Boolean(user),
  });

  const holdsQuery = useQuery({
    queryKey: ["me", "holds"],
    queryFn: () => api<{ active: Hold[] }>("/api/me/holds"),
    enabled: Boolean(user),
    refetchInterval: 60_000,
  });

  const apptQuery = useQuery({
    queryKey: ["me", "appointments"],
    queryFn: () => api<{ appointments: Appointment[] }>("/api/appointments/mine"),
    enabled: Boolean(user),
  });

  const qc = useQueryClient();
  const [walkInState, setWalkInState] = useState<Record<string, "idle" | "pending" | "done" | "error">>({});
  const walkIn = useMutation({
    mutationFn: (holdId: string) =>
      api<{ notified: true }>("/api/walk-in", { method: "POST", body: { holdId } }),
    onMutate: (holdId) => {
      setWalkInState((s) => ({ ...s, [holdId]: "pending" }));
    },
    onSuccess: (_data, holdId) => {
      setWalkInState((s) => ({ ...s, [holdId]: "done" }));
    },
    onError: (_err, holdId) => {
      setWalkInState((s) => ({ ...s, [holdId]: "error" }));
    },
  });

  const cancelAppt = useMutation({
    mutationFn: (id: string) =>
      api<{ status: "cancelled" }>(`/api/appointments/${id}/cancel`, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["me", "appointments"] }),
  });

  if (loading) return <div className="container"><p>{t("common.loading")}</p></div>;

  if (!user) {
    return (
      <div className="container">
        <div className="surface" style={{ maxWidth: "32rem", margin: "0 auto", textAlign: "center" }}>
          <h1>{t("dashboard.loginRequiredTitle")}</h1>
          <p>{t("dashboard.loginRequiredBody")}</p>
          <Link to="/login" className="btn btn-primary">{t("nav.login")}</Link>
        </div>
      </div>
    );
  }

  const locale = i18n.resolvedLanguage ?? "pt";

  return (
    <div className="container dashboard">
      <header className="dashboard__hero surface">
        <div>
          <p className="dashboard__greeting">{t("dashboard.greeting")}</p>
          <h1>{user.email}</h1>
        </div>
        <div className="dashboard__points">
          <span className="dashboard__points-value">{user.points}</span>
          <span className="dashboard__points-label">
            {user.points === 1 ? t("dashboard.points", { count: 1 }).replace("1 ", "") : t("dashboard.points", { count: user.points }).replace(`${user.points} `, "")}
          </span>
        </div>
      </header>

      <section aria-labelledby="holds-title">
        <h2 id="holds-title">{t("dashboard.holdsTitle")}</h2>
        {holdsQuery.isPending ? (
          <p>{t("dashboard.holdsLoading")}</p>
        ) : !holdsQuery.data || holdsQuery.data.active.length === 0 ? (
          <div className="surface dashboard__empty">
            <p>
              <Trans
                i18nKey="dashboard.holdsEmpty"
                components={{ 1: <Link to="/browse" /> }}
              />
            </p>
          </div>
        ) : (
          <ul className="dashboard__holds">
            {holdsQuery.data.active.map((h) => {
              const r = formatRelative(h.expiresAt, locale);
              const hasAppt = apptQuery.data?.appointments.some(
                (a) => a.holdId === h.id && a.status === "confirmed",
              );
              const wState = walkInState[h.id] ?? "idle";
              return (
                <li key={h.id} className="surface dashboard__hold">
                  {h.bookCoverUrl && (
                    <img src={`/api/cover/${h.bookId}`} alt="" className="dashboard__hold-cover" />
                  )}
                  <div className="dashboard__hold-body">
                    <h3>
                      <Link to={`/book/${h.bookId}`}>{h.bookTitle}</Link>
                    </h3>
                    {h.bookAuthor && <p className="dashboard__muted">{h.bookAuthor}</p>}
                    <p>
                      <strong className={r.soon ? "dashboard__urgent" : undefined}>
                        {t("dashboard.holdRemaining", { remaining: r.text })}
                      </strong>
                    </p>
                    {!hasAppt && wState !== "done" && (
                      <div className="dashboard__hold-actions">
                        <Link
                          to={`/marcacoes?type=pickup&holdId=${h.id}`}
                          className="btn btn-primary btn-sm"
                        >
                          Marcar levantamento
                        </Link>
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          onClick={() => walkIn.mutate(h.id)}
                          disabled={wState === "pending"}
                        >
                          {wState === "pending" ? t("dashboard.notifying") : t("dashboard.walkIn")}
                        </button>
                      </div>
                    )}
                    {hasAppt && (
                      <p className="dashboard__muted">
                        <small>{t("dashboard.activeAppt")}</small>
                      </p>
                    )}
                    {wState === "done" && (
                      <p className="dashboard__muted">
                        <small>{t("dashboard.notified")}</small>
                      </p>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {apptQuery.data && apptQuery.data.appointments.some((a) => a.status === "confirmed") && (
        <section aria-labelledby="appts-title">
          <h2 id="appts-title">{t("dashboard.upcomingAppts")}</h2>
          <ul className="dashboard__appts">
            {apptQuery.data.appointments
              .filter((a) => a.status === "confirmed")
              .map((a) => {
                const start = new Date(a.startTime);
                return (
                  <li key={a.id} className="surface dashboard__appt">
                    <div>
                      <strong>
                        {start.toLocaleDateString(locale === "en" ? "en-GB" : "pt-PT", {
                          weekday: "long",
                          day: "2-digit",
                          month: "long",
                        })}
                      </strong>
                      {" — "}
                      {new Intl.DateTimeFormat(locale === "en" ? "en-GB" : "pt-PT", {
                        timeZone: "Europe/Lisbon",
                        hour: "2-digit",
                        minute: "2-digit",
                      }).format(start)}
                      {a.bookTitle && (
                        <>
                          {" · "}
                          <em>{a.bookTitle}</em>
                        </>
                      )}
                      {" · "}
                      <span>{a.type === "pickup" ? t("dashboard.pickup") : t("dashboard.dropoff")}</span>
                    </div>
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={() => {
                        if (window.confirm(t("dashboard.cancelConfirm"))) cancelAppt.mutate(a.id);
                      }}
                      disabled={cancelAppt.isPending}
                    >
                      {t("common.cancel")}
                    </button>
                  </li>
                );
              })}
          </ul>
        </section>
      )}

      <DeleteAccountSection />

      <section aria-labelledby="tx-title">
        <h2 id="tx-title">{t("dashboard.txTitle")}</h2>
        {txQuery.isPending ? (
          <p>{t("dashboard.txLoading")}</p>
        ) : !txQuery.data || txQuery.data.transactions.length === 0 ? (
          <div className="surface dashboard__empty">
            <p>{t("dashboard.txEmpty")}</p>
          </div>
        ) : (
          <table className="dashboard__tx" aria-label={t("dashboard.txTitle")}>
            <thead>
              <tr>
                <th scope="col">{t("dashboard.txCols.date")}</th>
                <th scope="col">{t("dashboard.txCols.reason")}</th>
                <th scope="col">{t("dashboard.txCols.book")}</th>
                <th scope="col">{t("dashboard.txCols.points")}</th>
              </tr>
            </thead>
            <tbody>
              {txQuery.data.transactions.map((tx) => (
                <tr key={tx.id}>
                  <td>{formatDate(tx.createdAt, locale)}</td>
                  <td>{t(`dashboard.reason.${tx.reason}`)}</td>
                  <td>{tx.bookTitle ?? "—"}</td>
                  <td className={tx.delta > 0 ? "dashboard__pos" : "dashboard__neg"}>
                    {tx.delta > 0 ? "+" : ""}{tx.delta}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
